<template>
  <div class="main container-fluid profile-sub-page">
    <header class="page-hero">
      <div>
        <h4 class="page-hero__title mb-1">Смена пароля</h4>
        <p class="page-hero__subtitle mb-0">Обновите пароль для входа в аккаунт</p>
      </div>
      <router-link :to="{ name: 'Profile' }" class="btn btn-light btn-sm page-hero__back">
        <i class="bi bi-arrow-left me-1"></i>
        В профиль
      </router-link>
    </header>

    <section class="px-3 pb-3">
      <div class="row g-3">
        <div class="col-12 col-xl-7">
          <div class="panel-card h-100">
            <div class="panel-card__body">
              <div class="cloud-title">Форма обновления</div>

              <div v-if="passwordSuccess" class="alert alert-success py-2 px-3 mb-3">{{ passwordSuccess }}</div>
              <div v-if="passwordError" class="alert alert-danger py-2 px-3 mb-3">{{ passwordError }}</div>

              <form @submit.prevent="changePassword">
                <div class="field">
                  <label class="left-headers form-label mb-0">Email</label>
                  <input
                    v-model.trim="passwordForm.email"
                    type="email"
                    class="form-control form-control-sm"
                    autocomplete="email"
                    required
                    disabled
                  />
                </div>
                <div class="field">
                  <label class="left-headers form-label mb-0">Текущий пароль</label>
                  <input
                    v-model="passwordForm.oldPassword"
                    type="password"
                    class="form-control form-control-sm"
                    autocomplete="current-password"
                    required
                  />
                </div>
                <div class="field">
                  <label class="left-headers form-label mb-0">Новый пароль</label>
                  <input
                    v-model="passwordForm.newPassword"
                    type="password"
                    class="form-control form-control-sm"
                    autocomplete="new-password"
                    required
                  />
                </div>
                <div class="field">
                  <label class="left-headers form-label mb-0">Повторите новый пароль</label>
                  <input
                    v-model="passwordForm.confirmPassword"
                    type="password"
                    class="form-control form-control-sm"
                    autocomplete="new-password"
                    required
                  />
                </div>
                <button class="btn btn-primary btn-sm mt-1" type="submit" :disabled="passwordLoading">
                  <span v-if="passwordLoading" class="spinner-border spinner-border-sm me-1"></span>
                  Сохранить пароль
                </button>
              </form>
            </div>
          </div>
        </div>

        <div class="col-12 col-xl-5">
          <aside class="panel-card tips-card h-100">
            <div class="panel-card__body">
              <div class="cloud-title">
                <i class="bi bi-shield-check me-1"></i>
                Рекомендации
              </div>
              <ul class="tips-list mb-0">
                <li>Используйте длинный пароль (от 12 символов).</li>
                <li>Не повторяйте старый пароль в других сервисах.</li>
                <li>Добавьте цифры, буквы в разных регистрах и спецсимволы.</li>
                <li>Не передавайте пароль третьим лицам.</li>
              </ul>
            </div>
          </aside>
        </div>
      </div>
    </section>
  </div>
</template>

<script setup>
import axios from 'axios'
import { onMounted, reactive, ref } from 'vue'
import { useStore } from 'vuex'

const store = useStore()

const passwordLoading = ref(false)
const passwordError = ref('')
const passwordSuccess = ref('')
const passwordForm = reactive({
  email: '',
  oldPassword: '',
  newPassword: '',
  confirmPassword: '',
})

function extractErrorMessage(error, fallback) {
  const message = error?.response?.data?.message || error?.response?.data?.error || error?.response?.data?.data
  return String(message || fallback)
}

async function loadUserIdentity() {
  const fromStore = store.getters.currentUser
  if (fromStore?.email) {
    passwordForm.email = fromStore.email
    return
  }
  if (fromStore?.username && String(fromStore.username).includes('@')) {
    passwordForm.email = fromStore.username
  }
  try {
    const { data } = await axios.get('/api/account/me')
    const email = data?.email || data?.username || ''
    if (email) passwordForm.email = String(email)
  } catch {
    // no-op
  }
}

async function changePassword() {
  passwordError.value = ''
  passwordSuccess.value = ''

  if (!passwordForm.email.trim()) {
    passwordError.value = 'Укажите email.'
    return
  }
  if (!passwordForm.oldPassword || !passwordForm.newPassword) {
    passwordError.value = 'Заполните текущий и новый пароль.'
    return
  }
  if (passwordForm.newPassword !== passwordForm.confirmPassword) {
    passwordError.value = 'Подтверждение нового пароля не совпадает.'
    return
  }
  if (passwordForm.oldPassword === passwordForm.newPassword) {
    passwordError.value = 'Новый пароль должен отличаться от текущего.'
    return
  }

  passwordLoading.value = true
  try {
    const payload = {
      email: passwordForm.email.trim(),
      oldPassword: passwordForm.oldPassword,
      newPassword: passwordForm.newPassword,
    }
    const { data } = await axios.post('/api/account/change-password', payload)
    passwordSuccess.value = String(data?.data || 'Пароль успешно обновлен.')
    passwordForm.oldPassword = ''
    passwordForm.newPassword = ''
    passwordForm.confirmPassword = ''
  } catch (error) {
    passwordError.value = extractErrorMessage(error, 'Не удалось изменить пароль.')
  } finally {
    passwordLoading.value = false
  }
}

onMounted(async () => {
  await loadUserIdentity()
})
</script>

<style scoped>
/* ── Shared profile sub-page shell (matches ProfilePage / streams pages) ──── */
.profile-sub-page {
  min-height: 100%;
  padding-top: 10px;
  background:
    radial-gradient(circle at 2% 3%, rgba(66, 153, 225, 0.2), transparent 36%),
    radial-gradient(circle at 98% 2%, rgba(56, 189, 248, 0.18), transparent 32%),
    #f3f7ff;
}

.page-hero {
  margin: 0 20px 12px;
  padding: 14px 16px;
  border-radius: 16px;
  background: linear-gradient(135deg, #10305f 0%, #1b4f93 60%, #1e67b7 100%);
  color: #fff;
  border: 1px solid rgba(255, 255, 255, 0.12);
  box-shadow: 0 18px 40px rgba(16, 48, 95, 0.22);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}

.page-hero__title { font-weight: 800; letter-spacing: 0.01em; }
.page-hero__subtitle { color: rgba(232, 242, 255, 0.92); font-size: 13px; }
.page-hero__back { color: #103f77; border-color: #cfe2ff; font-weight: 700; white-space: nowrap; }

.panel-card {
  border-radius: 16px;
  border: 1px solid #dfe7f5;
  background: #fff;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.08);
  overflow: hidden;
}

.panel-card__body { padding: 14px; }

.cloud-title {
  display: inline-flex;
  align-items: center;
  margin: 0 0 12px;
  padding: 6px 10px;
  border-radius: 999px;
  border: 1px solid #d8e5fb;
  background: linear-gradient(180deg, #f2f7ff 0%, #eaf2ff 100%);
  color: #2f4f88;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.01em;
  box-shadow: 0 4px 10px rgba(49, 94, 227, 0.08);
}

.left-headers {
  padding-left: 1px;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.01em;
  color: #4d5f82;
  text-transform: uppercase;
}

/* ── Page-specific ────────────────────────────────────────────────────────── */
.field {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-bottom: 10px;
}

.panel-card__body .form-control.form-control-sm {
  min-height: 36px;
  font-size: 13.5px;
  border-radius: 10px;
  border-color: #d4e1f6;
}

.panel-card__body .form-control.form-control-sm:focus {
  border-color: #95b8ff;
  box-shadow: 0 0 0 0.2rem rgba(13, 110, 253, 0.14);
}

.panel-card__body .form-control.form-control-sm:disabled {
  background: #f5f8ff;
  color: #5f7393;
  opacity: 1;
}

.panel-card__body .btn.btn-sm {
  font-size: 13px;
  font-weight: 700;
  padding: 0.38rem 0.78rem;
}

.panel-card__body .alert {
  font-size: 13px;
  line-height: 1.35;
}

.tips-card {
  background: linear-gradient(155deg, #f3f9ff 0%, #ffffff 100%);
}

.tips-list {
  padding-left: 18px;
  color: #4e6384;
  font-size: 13.5px;
  line-height: 1.5;
}

.tips-list li + li { margin-top: 6px; }

@media (max-width: 992px) {
  .page-hero {
    margin: 0 12px 12px;
    flex-direction: column;
    align-items: flex-start;
  }
}
</style>
